import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { t } from '@lingui/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import Calendar from '@rover/icons/svg/calendar.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import focusOnElement from '../../../utilities/focusOnElement';
import { formatDateRange } from '../../StickyFiltersBar/utils';
import TruncatedText from '../../TruncatedText';
import {
  SearchTopFiltersDatesGroupDismiss,
  SearchTopFiltersDatesGroupOpen,
  SearchTopFiltersDatesGroupSave,
} from '../analyticsEvents';

import DatesDropdownContent from './DatesDropdownContent';
import FilterDropdown from './FilterDropdown';
import FilterSection from './FilterSection';

type DatesSectionProps = {
  searchFilters: SearchFilters;
  language: string;
  updateFiltersAndFireSearchOnChange: (filters: Partial<SearchFilters>) => void;
  serviceType: ServiceType;
  serviceTypeFilterOptions: SearchFiltersOptions;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (value: boolean) => void;
};

const DatesSection = ({
  searchFilters,
  language,
  updateFiltersAndFireSearchOnChange,
  serviceType,
  serviceTypeFilterOptions,
  isDropdownOpen,
  setIsDropdownOpen,
}: DatesSectionProps): JSX.Element => {
  const [localFilters, setLocalFilters] = useState<Partial<SearchFilters>>({});
  const sectionRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dialogId = useId();
  const theme = useTheme();
  const { startDate, endDate } = searchFilters;
  const dateValue = formatDateRange(startDate, endDate, language);

  useEffect(() => {
    if (isDropdownOpen) {
      setLocalFilters({
        frequency: searchFilters.frequency,
        startDate: searchFilters.startDate,
        endDate: searchFilters.endDate,
        selectedDays: searchFilters.selectedDays,
        morningAvailability: searchFilters.morningAvailability,
        middayAvailability: searchFilters.middayAvailability,
        eveningAvailability: searchFilters.eveningAvailability,
        spacesRequired: searchFilters.spacesRequired,
      });

      focusOnElement(dropdownRef);
    }
  }, [isDropdownOpen, searchFilters]);

  const mergedFilters = useMemo(() => {
    return { ...searchFilters, ...localFilters };
  }, [searchFilters, localFilters]);

  const handleFiltersChange = useCallback((filters: Partial<SearchFilters>) => {
    setLocalFilters((prev) => ({ ...prev, ...filters }));
  }, []);

  const handleSave = useCallback(() => {
    emitAnalyticsEvent(new SearchTopFiltersDatesGroupSave());
    updateFiltersAndFireSearchOnChange(localFilters);
    setIsDropdownOpen(false);

    focusOnElement(sectionRef);
  }, [localFilters, updateFiltersAndFireSearchOnChange, setIsDropdownOpen]);

  const handleSectionClick = (): void => {
    if (!isDropdownOpen) {
      emitAnalyticsEvent(new SearchTopFiltersDatesGroupOpen());
    } else {
      // We should also fire the dismiss event if the popover is closed by clicking
      // the button again.
      emitAnalyticsEvent(new SearchTopFiltersDatesGroupDismiss());
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClose = (): void => {
    // Track when user closes without saving
    emitAnalyticsEvent(new SearchTopFiltersDatesGroupDismiss());
    setIsDropdownOpen(false);

    focusOnElement(sectionRef);
  };

  return (
    <Box position="relative" flex="1" height="100%">
      <FilterSection onClick={handleSectionClick} ref={sectionRef} isOpen={isDropdownOpen}>
        <Flex alignItems="center" gap="2x" width="100%" data-testID="dates-section">
          <Box flexShrink="0">
            <Calendar
              width={24}
              height={24}
              fill={theme.colors.brand.active_brand}
              color={theme.colors.brand.active_brand}
            />
          </Box>
          <TruncatedText
            size="100"
            textColor={dateValue ? 'primary' : 'placeholder'}
            textAlign="left"
          >
            {dateValue || t`Add dates`}
          </TruncatedText>
        </Flex>
      </FilterSection>

      {isDropdownOpen && (
        <FilterDropdown
          ref={dropdownRef}
          onClose={handleClose}
          triggerRef={sectionRef}
          width="640px"
          align="right"
          role="dialog"
          id={dialogId}
          aria-label={t`Date filters`}
        >
          <DatesDropdownContent
            searchFilters={mergedFilters}
            language={language}
            onChange={handleFiltersChange}
            onSave={handleSave}
            serviceType={serviceType}
            serviceTypeFilterOptions={serviceTypeFilterOptions}
          />
        </FilterDropdown>
      )}
    </Box>
  );
};

export default DatesSection;
